import {isIframed} from './isIframed';
import {
  BusMessage,
  ParentMessageBus,
  ParentMessageListenerFn,
} from './ParentMessageBus';

export function createParentMessageBus({
  audience,
  entryPoint,
  parentOrigin = window.origin,
}: {
  audience?: string;
  entryPoint?: string;
  parentOrigin: string;
}): ParentMessageBus {
  const listeners: ParentMessageListenerFn[] = [];

  function addListener(listener: ParentMessageListenerFn) {
    listeners.push(listener);
  }

  function removeListener(listener: ParentMessageListenerFn) {
    const index = listeners.findIndex((l) => l === listener);
    if (index >= 0) {
      listeners.splice(index, 1);
    }
  }

  function post(message: BusMessage) {
    if (isIframed() && parentOrigin) {
      const wrapped = {
        ...message,
        audience,
        entryPoint,
      };
      window.parent.postMessage(wrapped, parentOrigin);
    }
  }

  function request(message: BusMessage, expectedResponseType: string) {
    const requestTimeoutInMilliseconds = 500;

    return new Promise<BusMessage>((resolve, reject) => {
      if (!isIframed()) {
        reject(new Error('Not iframed'));
      }

      const timeoutId = setTimeout(() => {
        removeListener(requestHandler);
        reject(new Error('Timed out waiting for a response'));
      }, requestTimeoutInMilliseconds);

      function requestHandler(fromParent: BusMessage) {
        if (fromParent.type === expectedResponseType) {
          clearTimeout(timeoutId);
          removeListener(requestHandler);
          resolve(fromParent);
        }
      }

      addListener(requestHandler);
      post(message);
    });
  }

  function handler(e: MessageEvent) {
    if (e.origin !== parentOrigin) {
      return;
    }

    if (!!e.data.audience && !!audience && e.data.audience !== audience) {
      return;
    }

    if (
      !!e.data.entryPoint &&
      !!entryPoint &&
      e.data.entryPoint !== entryPoint
    ) {
      return;
    }

    listeners.slice().forEach((l) => l(e.data));
  }
  window.addEventListener('message', handler);

  return {
    post,
    request,
    addListener,
    removeListener,
  };
}
